<template>
    <kiosk-group-enable-billing v-if="group && !group.is_billable" :teamId="props.teamId" :groupId="props.groupId" />

    <kiosk-group-details v-else-if="group && group.is_billable" :teamId="props.teamId" :groupId="props.groupId">
        <div class="col-span-12 lg:col-span-10 flex flex-col w-full justify-center">
            <div class="max-w-(--breakpoint-sm) flex flex-col">
                <page-callout section="kiosk-group-billing-config" class="mb-4" />

                <page-header>Billing Config</page-header>

                <vx-expandable-card class="mb-4">
                    <template v-slot:header>
                        <span :id="paymentMethodLabelId">Payment Method &amp; Terms</span>
                    </template>
                    <template v-slot:content>
                        <div class="flex flex-col gap-6 mt-3">
                            <div
                                role="radiogroup"
                                :aria-labelledby="paymentMethodLabelId"
                                class="flex flex-col gap-2"
                                data-test="payment-methods"
                            >
                                <label class="flex items-center gap-2 pb-2">
                                    <input
                                        type="radio"
                                        v-model="billingOption"
                                        :name="billingOptionName"
                                        value="automatic"
                                        :disabled="team?.status === 'closed'"
                                        class="w-4 h-4"
                                    />
                                    <span>Automatic card payment (due immediately)</span>
                                </label>
                                <div class="flex items-start gap-2 pb-2">
                                    <input
                                        type="radio"
                                        :id="invoiceOptionId"
                                        v-model="billingOption"
                                        :name="billingOptionName"
                                        value="invoice"
                                        :disabled="team?.status === 'closed'"
                                        class="w-4 h-4 mt-1"
                                    />
                                    <div class="flex flex-col gap-2 flex-1">
                                        <label :for="invoiceOptionId">Invoice with payment terms</label>
                                        <div v-if="billingOption === 'invoice'" class="flex items-center gap-2">
                                            <span :id="paymentTermsLabelId" class="text-sm">Payment terms:</span>
                                            <vx-input
                                                v-model="form.payment_terms_days"
                                                :aria-labelledby="paymentTermsLabelId"
                                                :aria-describedby="
                                                    paymentTermsError
                                                        ? `${paymentTermsHintId} ${paymentTermsErrorId}`
                                                        : paymentTermsHintId
                                                "
                                                type="number"
                                                :min="1"
                                                :max="120"
                                                size="sm"
                                                class="w-20"
                                                :disabled="team?.status === 'closed'"
                                                @blur="validatePaymentTerms"
                                            />
                                            <span class="text-sm">days</span>
                                        </div>
                                        <span
                                            v-if="billingOption === 'invoice'"
                                            :id="paymentTermsHintId"
                                            class="text-xs text-gray-500"
                                        >
                                            e.g., 30 for Net 30, 60 for Net 60 (max 120 days)
                                        </span>
                                        <span
                                            v-if="paymentTermsError && billingOption === 'invoice'"
                                            :id="paymentTermsErrorId"
                                            class="text-xs text-red-600"
                                        >
                                            {{ paymentTermsError }}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div v-if="team?.status !== 'closed'">
                                <vx-button
                                    data-test="submit-button"
                                    @click="submit"
                                    size="lg"
                                    color="primary"
                                    :loading="submitting"
                                    :disabled="!!paymentTermsError"
                                    class="w-full"
                                >
                                    Update
                                </vx-button>
                            </div>
                        </div>
                    </template>
                </vx-expandable-card>

                <kiosk-group-pricing-card
                    v-if="group && team"
                    :team="team"
                    :teamId="props.teamId"
                    :group="group"
                    :groupId="props.groupId"
                    @updated="refreshGroup(props.teamId, props.groupId)"
                />
            </div>
        </div>
    </kiosk-group-details>
</template>

<script setup>
import { ref, useId, watch } from 'vue';
import KioskGroupDetails from '~/components/kiosk/kiosk-groups/details/KioskGroupDetails.vue';
import KioskGroupEnableBilling from './KioskGroupEnableBilling.vue';
import KioskGroupPricingCard from './KioskGroupPricingCard.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import PageCallout from '~/components/general/PageCallout.vue';
import { useGroupDetails } from '~/components/kiosk/kiosk-groups/composables/useGroupDetails';
import { VxButton, VxExpandableCard, VxInput } from '@voxie/frontend-components';
import adminGroupsClient from '~/services/adminGroupsClient';
import { useToasted } from '~/composables/useToasted';
import { catchRequestError } from '~/utils/request';

const props = defineProps({
    teamId: { type: Number, required: true },
    groupId: { type: String, required: true },
});

const { group, team, onMountedGroup, refreshGroup } = useGroupDetails();
const toasted = useToasted();

const form = ref({
    payment_terms_days: '0',
});

const submitting = ref(false);
const paymentTermsError = ref(null);
const billingOption = ref('automatic');

const paymentMethodLabelId = useId();
const billingOptionName = useId();
const invoiceOptionId = useId();
const paymentTermsLabelId = useId();
const paymentTermsHintId = useId();
const paymentTermsErrorId = useId();

watch(billingOption, (newValue) => {
    if (newValue === 'automatic') {
        form.value.payment_terms_days = '0';
    } else if (newValue === 'invoice') {
        if (form.value.payment_terms_days === '0' || form.value.payment_terms_days === 0) {
            form.value.payment_terms_days = '30';
        }
    }
});

const validatePaymentTerms = () => {
    const days = parseInt(form.value.payment_terms_days) || 0;
    if (days > 0) {
        if (days > 120) {
            paymentTermsError.value = 'Payment terms cannot exceed 120 days';
        } else {
            paymentTermsError.value = null;
        }
    } else {
        paymentTermsError.value = null;
    }
};

const submit = async () => {
    if (submitting.value) {
        return;
    }

    validatePaymentTerms();
    if (paymentTermsError.value) {
        return;
    }

    submitting.value = true;

    try {
        const data = {
            payment_terms_days: parseInt(form.value.payment_terms_days) || 0,
        };

        await adminGroupsClient.updateBillingOptions(props.teamId, props.groupId, data);

        await refreshGroup(props.teamId, props.groupId);

        toasted.success('Payment terms have been successfully updated');
    } catch (e) {
        toasted.error(catchRequestError(e));
    } finally {
        submitting.value = false;
    }
};

onMountedGroup(() => {
    form.value = {
        payment_terms_days: String(group.value.payment_terms_days || 0),
    };

    if (group.value.payment_terms_days === 0) {
        billingOption.value = 'automatic';
    } else {
        billingOption.value = 'invoice';
    }
});
</script>
